import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuroraBackground from '../components/AuroraBackground/AuroraBackground';
import AuroraNavbar from '../components/AuroraNavbar/AuroraNavbar';
import Footer from '../components/Footer';
import MetaHead from '../components/MetaHead';
import { PLAN_BY_ID } from '../constants/plans';
import { formatPriceParts } from '../utils/format';
import '../styles/pg-pricing.css';

export default function Pricing() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['pricing', 'common']);
  const lang = i18n.language || 'fr';

  // Rend un prix de plan avec le symbole € positionné selon la locale
  // (« 9,99 € » en fr/es/it, « €9.99 » en en). Le symbole reste dans son
  // propre span pour conserver la typo de la grille.
  const renderPrice = (price) => {
    const { value, currency, currencyFirst } = formatPriceParts(price, lang);
    return currencyFirst ? (
      <span className="pg-pricing-amount">{currency}{value}</span>
    ) : (
      <span className="pg-pricing-amount">{value}{currency}</span>
    );
  };
  const [openFaq, setOpenFaq] = useState(null);

  const souscrire = (plan) => {
    navigate('/register?plan=' + plan);
  };

  // FAQ : lecture liste depuis pricing.faq.items
  const faqs = t('faq.items', { returnObjects: true });
  const faqList = Array.isArray(faqs) ? faqs : [];

  // Plans features (arrays i18n)
  // ⚠️ Bloc « Découverte » (9,99 €) : remplace l'ancien palier « Essentiel »
  // (69 €) retiré de la grille publique le 30/07/2026. Les abonnés Essentiel
  // restent facturés normalement — cf. constants/plans.js (legacy: true).
  const decouverteFeatures = t('decouverte.features', { returnObjects: true }) || [];
  const proFeatures = t('pro.features', { returnObjects: true }) || [];
  const intensifFeatures = t('intensif.features', { returnObjects: true }) || [];

  return (
    <>
      <MetaHead page="pricing" />
      <AuroraBackground />
      <AuroraNavbar activeLink="pricing" />

      <main className="pg-pricing">

        {/* HERO */}
        <section className="pg-pricing-hero">
          <h1 className="pg-pricing-h1">{t('hero.title')}</h1>
          <p className="pg-pricing-hero-sub">{t('hero.subtitle')}</p>
        </section>

        {/* TARIFS */}
        <section className="pg-pricing-section">
          <div className="pg-pricing-grid">

            {/* DÉCOUVERTE */}
            <div className="pg-pricing-card">
              <div className="pg-pricing-tier-name">{t('decouverte.name')}</div>
              <div className="pg-pricing-price">
                {renderPrice(PLAN_BY_ID.decouverte.price)}
                <span className="pg-pricing-period">{t('period')}</span>
              </div>
              <div className="pg-pricing-tagline">{t('decouverte.tagline')}</div>
              <div className="pg-pricing-trial">{t('trial')}</div>
              <div className="pg-pricing-sep" />
              <ul className="pg-pricing-list">
                {decouverteFeatures.map((feat, i) => (
                  <li key={i}><span className="pg-pricing-dash">—</span>{feat}</li>
                ))}
              </ul>
              <button className="pg-pricing-btn-outline" onClick={() => souscrire('decouverte')}>
                {t('decouverte.cta')}
              </button>
            </div>

            {/* PRO (featured) */}
            <div className="pg-pricing-card pg-pricing-card-featured">
              <div className="pg-pricing-badge">{t('popular_badge')}</div>
              <div className="pg-pricing-tier-name">{t('pro.name')}</div>
              <div className="pg-pricing-price">
                {renderPrice(PLAN_BY_ID.pro.price)}
                <span className="pg-pricing-period">{t('period')}</span>
              </div>
              <div className="pg-pricing-tagline">{t('pro.tagline')}</div>
              <div className="pg-pricing-trial">{t('trial')}</div>
              <div className="pg-pricing-sep" />
              <ul className="pg-pricing-list">
                {proFeatures.map((feat, i) => (
                  <li key={i}><span className="pg-pricing-dash">—</span>{feat}</li>
                ))}
              </ul>
              <button className="pg-pricing-btn-cta" onClick={() => souscrire('pro')}>
                {t('pro.cta')}
              </button>
            </div>

            {/* INTENSIF */}
            <div className="pg-pricing-card">
              <div className="pg-pricing-tier-name">{t('intensif.name')}</div>
              <div className="pg-pricing-price">
                {renderPrice(PLAN_BY_ID.intensif.price)}
                <span className="pg-pricing-period">{t('period')}</span>
              </div>
              <div className="pg-pricing-tagline">{t('intensif.tagline')}</div>
              <div className="pg-pricing-trial">{t('trial')}</div>
              <div className="pg-pricing-sep" />
              <ul className="pg-pricing-list">
                {intensifFeatures.map((feat, i) => (
                  <li key={i}><span className="pg-pricing-dash">—</span>{feat}</li>
                ))}
              </ul>
              <button className="pg-pricing-btn-outline" onClick={() => souscrire('intensif')}>
                {t('intensif.cta')}
              </button>
            </div>

          </div>

          {/* GROUPES & MULTI-SITES */}
          <div className="pg-pricing-multi">
            <h3 className="pg-pricing-multi-title">{t('multi_site.title')}</h3>
            <p className="pg-pricing-multi-sub">{t('multi_site.subtitle')}</p>
            <div className="pg-pricing-multi-tiers">
              <span className="pg-pricing-multi-tag">{t('multi_site.discount_2_4')}</span>
              <span className="pg-pricing-multi-dot">·</span>
              <span className="pg-pricing-multi-tag">{t('multi_site.discount_5_9')}</span>
              <span className="pg-pricing-multi-dot">·</span>
              <span className="pg-pricing-multi-tag">{t('multi_site.devis_10')}</span>
            </div>
            <p className="pg-pricing-multi-pool">{t('multi_site.pool')}</p>
            <a href="mailto:adrien@getrevyo.com" className="pg-pricing-multi-btn">
              {t('multi_site.cta')}
            </a>
          </div>
        </section>

        {/* FAQ */}
        <section className="pg-pricing-faq">
          <div className="pg-pricing-faq-inner">
            <h2 className="pg-pricing-h2">{t('faq.title')}</h2>
            <div className="pg-pricing-faq-list">
              {faqList.map((item, i) => (
                <div key={i} className="pg-pricing-faq-item">
                  <div
                    className="pg-pricing-faq-q"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <span>{item.q}</span>
                    <span className="pg-pricing-faq-arrow">{openFaq === i ? '−' : '+'}</span>
                  </div>
                  {openFaq === i && (
                    <div className="pg-pricing-faq-a">{item.a}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA FINAL */}
        <section className="pg-pricing-cta">
          <h2 className="pg-pricing-cta-title">{t('cta_final.title')}</h2>
          <p className="pg-pricing-cta-sub">{t('cta_final.subtitle')}</p>
          <button className="pg-pricing-cta-btn" onClick={() => navigate('/register')}>
            {t('cta_final.cta')}
          </button>
        </section>

      </main>

      <Footer />
    </>
  );
}
